import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GraduationCap, TrendingUp, Award } from 'lucide-react';

interface KardexItem {
  enrollmentId: number;
  periodCode: string;
  periodName: string;
  courseCode: string;
  courseName: string;
  credits: number;
  enrollmentStatus: string;
  numericGrade: number | null;
  letterGrade: string | null;
  isPassing: boolean | null;
}

interface KardexStats {
  totalCourses: number;
  completedCourses: number;
  totalCredits: number;
  earnedCredits: number;
  averageGrade: number | null;
}

export default function AlumnoKardex() {
  const [kardex, setKardex] = useState<KardexItem[]>([]);
  const [stats, setStats] = useState<KardexStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchKardex();
  }, []);

  const fetchKardex = async () => {
    try {
      const response = await fetch('/api/grades/kardex', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar kardex');
      }

      const data = await response.json();
      setKardex(data.kardex || []);
      setStats(data.statistics || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar kardex');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, isPassing: boolean | null) => {
    if (status === 'completed') {
      return isPassing ? (
        <span className="px-2 py-1 text-xs rounded bg-green-500 text-white">Aprobado</span>
      ) : (
        <span className="px-2 py-1 text-xs rounded bg-red-500 text-white">Reprobado</span>
      );
    }
    if (status === 'enrolled') {
      return <span className="px-2 py-1 text-xs rounded bg-blue-500 text-white">Cursando</span>;
    }
    if (status === 'dropped') {
      return <span className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700">Dado de Baja</span>;
    }
    return <span className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700">{status}</span>;
  };

  const groupByPeriod = (items: KardexItem[]) => {
    const grouped: { [key: string]: KardexItem[] } = {};
    items.forEach(item => {
      if (!grouped[item.periodCode]) {
        grouped[item.periodCode] = [];
      }
      grouped[item.periodCode].push(item);
    });
    return grouped;
  };

  if (loading) {
    return <div className="text-center py-8">Cargando kardex...</div>;
  }

  const groupedKardex = groupByPeriod(kardex);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Kardex de Calificaciones</h1>
        <p className="text-gray-600 mt-2">Consulta tu historial académico completo</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Promedio</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-3xl font-bold">
                  {stats.averageGrade || 'N/A'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Materias</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <GraduationCap className="w-8 h-8 text-green-600 mr-3" />
                <span className="text-3xl font-bold">
                  {stats.totalCourses}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Aprobadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Award className="w-8 h-8 text-purple-600 mr-3" />
                <span className="text-3xl font-bold">
                  {stats.completedCourses}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Créditos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <span className="text-3xl font-bold">
                  {stats.earnedCredits} / {stats.totalCredits}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {kardex.length === 0 ? (
        <Alert>
          <AlertDescription>
            No hay calificaciones registradas aún.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedKardex).map(([periodCode, items]) => (
            <Card key={periodCode}>
              <CardHeader>
                <CardTitle>{items[0].periodName}</CardTitle>
                <CardDescription>Código: {periodCode}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Código</th>
                        <th className="text-left py-3 px-4">Materia</th>
                        <th className="text-center py-3 px-4">Créditos</th>
                        <th className="text-center py-3 px-4">Calificación</th>
                        <th className="text-center py-3 px-4">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.enrollmentId} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm">{item.courseCode}</td>
                          <td className="py-3 px-4">{item.courseName}</td>
                          <td className="py-3 px-4 text-center">{item.credits}</td>
                          <td className="py-3 px-4 text-center">
                            {item.numericGrade ? (
                              <span className={`font-semibold ${item.isPassing ? 'text-green-600' : 'text-red-600'}`}>
                                {item.numericGrade}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {getStatusBadge(item.enrollmentStatus, item.isPassing)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
